import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RevealOnScroll } from "../shared";
import CaseStudyEvidence from "../../roofing/caseStudy/CaseStudyEvidence";
import {
  CASE_STUDY_BEFORE_AFTER,
  CASE_STUDY_CLIENT,
  CASE_STUDY_CLIENT_URL,
  CASE_STUDY_EVIDENCE_CAPTION,
  CASE_STUDY_EVIDENCE_LABEL,
  CASE_STUDY_FOOTNOTE,
  CASE_STUDY_METRICS,
  CASE_STUDY_OUTCOME,
  PARTNER_CASE_STUDY,
} from "./partnerCaseStudyContent";

const ClientLink = ({ children, className = "" }) => (
  <a href={CASE_STUDY_CLIENT_URL} target="_blank" rel="noreferrer" className={className}>
    {children}
  </a>
);

const MetricCard = ({ metric, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.35, delay: index * 0.08 }}
    className={`relative rounded-xl p-5 sm:p-6 border ${
      metric.highlight
        ? "border-[#00D2FF]/40 bg-[#00D2FF]/10 ring-1 ring-[#00D2FF]/20"
        : "border-partner bg-[#11151F]"
    }`}
    data-testid={index === 0 ? "partner-live-deployment-stat" : undefined}
  >
    {metric.highlight && (
      <span className="absolute -top-2.5 left-4 font-mono text-[9px] text-partner-accent bg-[#080A0F] border border-[#00D2FF]/30 px-2 py-0.5 rounded-full">
        KEY RESULT
      </span>
    )}
    <div
      className={`text-4xl sm:text-5xl font-bold font-mono mb-1 leading-none ${
        metric.highlight ? "text-partner-accent" : "text-partner"
      }`}
    >
      {metric.value}
    </div>
    <div className="text-sm font-semibold text-partner mb-0.5">{metric.label}</div>
    <div className="text-[11px] font-mono text-partner-muted">{metric.sub}</div>
  </motion.div>
);

const PartnerLiveDeploymentSection = ({ id = "live-deployment" }) => (
  <section
    id={id}
    className="section-padding section-alt relative overflow-hidden border-y border-partner"
    data-testid="partner-live-deployment-section"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <RevealOnScroll>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <p className="font-mono text-[10px] sm:text-xs text-partner-accent font-semibold tracking-wider mb-3">
              {PARTNER_CASE_STUDY.tag}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight max-w-2xl">
              {PARTNER_CASE_STUDY.headline}
            </h2>
            <p className="text-partner-muted font-body max-w-2xl leading-relaxed mt-4">
              {PARTNER_CASE_STUDY.subline}
            </p>
          </div>
          <p className="text-xs font-mono text-partner-muted sm:text-right max-w-[14rem] sm:pb-1">
            {PARTNER_CASE_STUDY.sidebarMeta}
          </p>
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.08}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10">
          {CASE_STUDY_METRICS.map((metric, index) => (
            <MetricCard key={metric.label} metric={metric} index={index} />
          ))}
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.12}>
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="lg:col-span-5 space-y-5">
            <div className="flex items-start gap-4 p-5 rounded-xl border border-partner bg-[#11151F]">
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#00D2FF]/15 border border-[#00D2FF]/30 flex items-center justify-center text-partner-accent font-bold text-lg">
                {CASE_STUDY_CLIENT.initials}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-mono text-partner-accent tracking-widest mb-1">CLIENT</p>
                <p className="text-lg font-bold text-partner leading-tight">
                  <ClientLink className="hover:text-partner-accent transition-colors">
                    {CASE_STUDY_CLIENT.name}
                  </ClientLink>
                </p>
                <p className="text-sm text-partner-muted font-body mt-1">{CASE_STUDY_CLIENT.region}</p>
              </div>
            </div>

            <p className="text-base text-partner-muted font-body leading-relaxed">
              {PARTNER_CASE_STUDY.leadParts.beforeLink}
              <ClientLink className="text-partner font-semibold hover:text-partner-accent transition-colors">
                {PARTNER_CASE_STUDY.leadParts.linkText}
              </ClientLink>{" "}
              {PARTNER_CASE_STUDY.leadParts.afterLink}
            </p>

            <div className="rounded-xl overflow-hidden border border-partner">
              <div className="grid grid-cols-2 text-center text-[10px] sm:text-xs font-mono tracking-wider">
                <div className="py-3 px-2 bg-red-500/10 text-red-400/90 border-b border-r border-partner">
                  <span className="block text-[9px] text-partner-muted mb-1">BEFORE</span>
                  <span className="line-through opacity-80">{CASE_STUDY_BEFORE_AFTER.before}</span>
                </div>
                <div className="py-3 px-2 bg-[#00D2FF]/10 text-partner-accent border-b border-partner">
                  <span className="block text-[9px] text-partner-muted mb-1">AFTER</span>
                  <span className="font-semibold">{CASE_STUDY_BEFORE_AFTER.after}</span>
                </div>
              </div>
              <div className="py-3 px-4 text-center bg-[#00D2FF]/5 border-t border-[#00D2FF]/20">
                <span className="text-xs sm:text-sm font-mono text-partner-accent font-semibold">
                  {CASE_STUDY_OUTCOME}
                </span>
              </div>
            </div>

            <p className="text-[11px] text-partner-muted font-mono">{CASE_STUDY_FOOTNOTE}</p>

            <a
              href={PARTNER_CASE_STUDY.productLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-partner-accent hover:underline"
              data-testid="partner-live-deployment-product-link"
            >
              {PARTNER_CASE_STUDY.productLabel}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <div className="lg:col-span-7">
            <div className="relative p-3 sm:p-4 rounded-2xl bg-[#11151F] border border-partner">
              <div className="absolute -top-3 left-6 font-mono text-[10px] text-partner-muted bg-[#080A0F] border border-partner px-3 py-1 rounded-full">
                {CASE_STUDY_EVIDENCE_LABEL}
              </div>
              <div data-testid="partner-live-deployment-evidence">
                <CaseStudyEvidence className="rounded-md" />
              </div>
              <p className="mt-3 text-center text-[10px] font-mono text-partner-muted tracking-wide">
                {CASE_STUDY_EVIDENCE_CAPTION}
              </p>
            </div>
          </div>
        </div>
      </RevealOnScroll>
    </div>
  </section>
);

export default PartnerLiveDeploymentSection;
